// src/components/AdminPanel/AdminThemes.js
import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import "../../css/okgather1.webflow.css";
import { adminApi } from "../../services/adminApi";

// Lazy load the RichTextEditor component to avoid build issues with react-quill
const RichTextEditor = lazy(() => import("./RichTextEditor"));

function AdminThemes({ adminToken, tabName, onTabChange, onDirtyChange }) {
  const [themes, setThemes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [venues, setVenues] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [newThemeId, setNewThemeId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Form state
  const [formMode, setFormMode] = useState("create");
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [formData, setFormData] = useState({
    theme_name: "",
    requires_subscription: true,
    theme_type: "",
    day_of_week: "",
    week_of_month: "",
    weekly: false,
    available_locations: [],
    image_url: "",
    default_venue_id: "",
    default_description: "",
  });
  // Per-location overrides: { [location_id]: { venue_id, day_of_week, week_of_month, weekly } }
  const [locationConfigs, setLocationConfigs] = useState({});

  // Edit-mode state for dirty tracking and stable ordering
  const [originalFormData, setOriginalFormData] = useState(null);
  const [originalLocationConfigs, setOriginalLocationConfigs] = useState(null);
  const [originalActiveLocIds, setOriginalActiveLocIds] = useState(new Set());
  const [editModeLocationOrder, setEditModeLocationOrder] = useState([]);

  // Unsaved-changes warning
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const pendingActionRef = useRef(null); // 'cancel' | { type: 'tabChange', tabName }

  // ── Dirty state ──────────────────────────────────────────────────────────
  const normAvailable = (arr) => [...arr].sort((a, b) => a - b);
  const isDirty = formMode === "edit" && originalFormData !== null && (
    JSON.stringify({ ...formData, available_locations: normAvailable(formData.available_locations) }) !==
    JSON.stringify({ ...originalFormData, available_locations: normAvailable(originalFormData.available_locations) }) ||
    JSON.stringify(locationConfigs) !== JSON.stringify(originalLocationConfigs)
  );

  // Notify parent whenever dirty state changes so AdminPanel can intercept tab clicks
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // Browser navigation guard
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  // Fetch themes
  const fetchThemes = async () => {
    if (!adminToken) return;
    try {
      setLoading(true);
      setError("");
      const response = await fetch("/admin/themes", {
        headers: {
          Authorization: `Bearer ${adminToken}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch themes");
      const data = await response.json();
      setThemes(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch locations for multi-select
  const fetchLocations = async () => {
    if (!adminToken) return;
    try {
      const data = await adminApi.getLocations(adminToken);
      setLocations(data.filter((loc) => loc.is_active));
    } catch (err) {
      console.error("Failed to load locations:", err);
    }
  };

  const fetchVenues = async () => {
    if (!adminToken) return;
    try {
      const data = await adminApi.getVenuesWithLocation(adminToken);
      setVenues(data);
    } catch (err) {
      console.error("Failed to load venues:", err);
    }
  };

  useEffect(() => {
    if (!adminToken) return;
    fetchThemes();
    fetchLocations();
    fetchVenues();
    // eslint-disable-next-line
  }, [adminToken]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleLocationToggle = (locationId) => {
    const id = parseInt(locationId);
    setFormData(prev => {
      const locs = prev.available_locations;
      const index = locs.indexOf(id);
      if (index > -1) {
        return { ...prev, available_locations: locs.filter(locId => locId !== id) };
      } else {
        return { ...prev, available_locations: [...locs, id] };
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!adminToken) return;

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const submitData = {
        ...formData,
        day_of_week: formData.day_of_week || null,
        week_of_month: formData.week_of_month ? parseInt(formData.week_of_month) : null,
        default_venue_id: formData.default_venue_id ? parseInt(formData.default_venue_id) : null,
      };

      let response;
      if (formMode === "create") {
        response = await fetch("/admin/themes", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify(submitData),
        });
      } else if (formMode === "edit" && selectedTheme) {
        response = await fetch(`/admin/themes/${selectedTheme.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
          body: JSON.stringify(submitData),
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to save theme");
      }

      const resultData = await response.json();
      const themeId = resultData.id;

      const activeLocIds = formData.available_locations;
      const locationEntries = activeLocIds.map(locId => ({
        location_id: locId,
        ...(locationConfigs[locId] || {}),
        venue_id: (locationConfigs[locId]?.venue_id) || null,
      }));
      if (locationEntries.length > 0) {
        await adminApi.updateThemeLocationVenues(themeId, locationEntries, adminToken);
      }

      if (formMode === "create") {
        setNewThemeId(themeId);
        if (onTabChange) {
          sessionStorage.setItem('newThemeMessage', JSON.stringify({
            themeId: themeId,
            themeName: resultData.theme_name,
            message: `Theme "${resultData.theme_name}" created! Please create a landing page for it to make it visible on the site.`
          }));
          onTabChange('themeLandingPages');
        } else {
          setSuccessMessage(`Theme created successfully! Please create a landing page for it to make it visible on the site.`);
        }
      } else {
        setSuccessMessage(`Theme updated successfully!`);
      }

      resetForm(true);
      fetchThemes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const startEdit = async (theme) => {
    setSelectedTheme(theme);
    setFormMode("edit");

    const newFormData = {
      theme_name: theme.theme_name,
      requires_subscription: theme.requires_subscription,
      theme_type: theme.theme_type,
      day_of_week: theme.day_of_week || "",
      week_of_month: theme.week_of_month || "",
      weekly: theme.weekly || false,
      available_locations: (theme.available_locations || []).map(id => parseInt(id)),
      image_url: theme.image_url || "",
      default_venue_id: theme.default_venue_id ? String(theme.default_venue_id) : "",
      default_description: theme.default_description || "",
    };
    setFormData(newFormData);
    setOriginalFormData(newFormData);

    // Load per-location configs
    let map = {};
    try {
      const rows = await adminApi.getThemeLocationVenues(theme.id, adminToken);
      rows.forEach(r => {
        map[r.location_id] = {
          venue_id: r.venue_id ? String(r.venue_id) : "",
          day_of_week: r.day_of_week || "",
          week_of_month: r.week_of_month || "",
          weekly: r.weekly || false,
          fortnightly: r.fortnightly || false,
          first_event_date: r.first_event_date || "",
        };
      });
    } catch {
      map = {};
    }
    setLocationConfigs(map);
    setOriginalLocationConfigs(map);

    // Capture which location IDs were already active on load
    const themeLocIds = (theme.available_locations || []).map(id => parseInt(id));
    const alreadyActiveIds = new Set(themeLocIds.filter(locId => {
      const cfg = map[locId] || {};
      return !!cfg.venue_id && !!cfg.day_of_week && !!(cfg.week_of_month || cfg.weekly || cfg.fortnightly) && !!cfg.first_event_date;
    }));
    setOriginalActiveLocIds(alreadyActiveIds);

    // Freeze the location display order at the moment editing begins
    const sortedOrder = [...locations].sort((a, b) => {
      const rank = (loc) => {
        const inc = themeLocIds.includes(loc.id);
        if (!inc) return 2;
        const cfg = map[loc.id] || {};
        const ok = !!cfg.venue_id && !!cfg.day_of_week && !!(cfg.week_of_month || cfg.weekly || cfg.fortnightly) && !!cfg.first_event_date;
        return ok ? 1 : 0;
      };
      return rank(a) - rank(b) || a.city.localeCompare(b.city);
    }).map(l => l.id);
    setEditModeLocationOrder(sortedOrder);

    setShowForm(true);
    setTimeout(() => { window.scrollTo({ top: 0, behavior: 'smooth' }); }, 100);
  };

  const handleClone = async (theme) => {
    if (!adminToken) return;

    let clonedName = theme.theme_name;
    if (clonedName.includes("(20s & 30s)")) {
      clonedName = clonedName.replace("(20s & 30s)", "(30s & 40s)");
    } else if (clonedName.includes("(30s & 40s)")) {
      clonedName = clonedName.replace("(30s & 40s)", "(20s & 30s)");
    } else {
      clonedName = clonedName + " (copy)";
    }

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const clonedData = {
        theme_name: clonedName,
        requires_subscription: theme.requires_subscription,
        theme_type: theme.theme_type,
        day_of_week: theme.day_of_week || null,
        week_of_month: theme.week_of_month || null,
        weekly: theme.weekly || false,
        available_locations: theme.available_locations || [],
        image_url: theme.image_url || "",
        default_venue_id: theme.default_venue_id || null,
        default_description: theme.default_description || null,
      };

      const response = await fetch("/admin/themes", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(clonedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to clone theme");
      }

      setSuccessMessage(`Theme cloned successfully as "${clonedName}"!`);
      fetchThemes();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLandingPage = (theme) => {
    if (onTabChange) {
      sessionStorage.setItem('newThemeMessage', JSON.stringify({
        themeId: theme.id,
        themeName: theme.theme_name,
        message: `Create a landing page for "${theme.theme_name}" to make it visible on the site.`
      }));
      onTabChange('themeLandingPages');
    }
  };

  // Force-reset without dirty check (called after save, or after user confirms discard)
  const resetForm = (force = false) => {
    setFormData({
      theme_name: "",
      requires_subscription: true,
      theme_type: "",
      day_of_week: "",
      week_of_month: "",
      weekly: false,
      available_locations: [],
      image_url: "",
      default_venue_id: "",
      default_description: "",
    });
    setLocationConfigs({});
    setOriginalFormData(null);
    setOriginalLocationConfigs(null);
    setOriginalActiveLocIds(new Set());
    setEditModeLocationOrder([]);
    setSelectedTheme(null);
    setFormMode("create");
    setShowForm(false);
  };

  // Cancel button handler — checks dirty state first
  const handleCancelClick = () => {
    if (isDirty) {
      pendingActionRef.current = 'cancel';
      setShowUnsavedWarning(true);
    } else {
      resetForm(true);
    }
  };

  // Called by AdminPanel when a tab change is attempted while dirty
  const handleTabChangeDirty = (tabName) => {
    pendingActionRef.current = { type: 'tabChange', tabName };
    setShowUnsavedWarning(true);
  };

  // Expose dirty-aware tab change handler to parent via ref (AdminPanel passes onRequestTabChange)
  const confirmDiscard = () => {
    setShowUnsavedWarning(false);
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    if (action === 'cancel') {
      resetForm(true);
    } else if (action?.type === 'tabChange') {
      resetForm(true);
      onTabChange(action.tabName);
    }
  };

  const cancelDiscard = () => {
    setShowUnsavedWarning(false);
    pendingActionRef.current = null;
  };

  const updateLocationConfig = (locId, field, value) => {
    setLocationConfigs(prev => ({
      ...prev,
      [locId]: { ...(prev[locId] || {}), [field]: value },
    }));
  };

  const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  function computeNextEvent(firstDateStr, dayOfWeek, weekly, fortnightly, weekOfMonth) {
    if (!firstDateStr || !dayOfWeek) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const firstDate = new Date(firstDateStr + 'T00:00:00');
    if (isNaN(firstDate.getTime())) return null;

    if (weekly) {
      let next = new Date(firstDate);
      while (next < today) next = new Date(next.getTime() + 7 * 86400000);
      return next;
    }
    if (fortnightly) {
      let next = new Date(firstDate);
      while (next < today) next = new Date(next.getTime() + 14 * 86400000);
      return next;
    }
    if (weekOfMonth) {
      const nth = parseInt(weekOfMonth);
      const targetDayIdx = DAYS.indexOf(dayOfWeek);
      if (targetDayIdx === -1) return null;
      let year = today.getFullYear();
      let month = today.getMonth();
      for (let i = 0; i < 13; i++) {
        const firstDay = new Date(year, month, 1);
        let diff = targetDayIdx - firstDay.getDay();
        if (diff < 0) diff += 7;
        const candidate = new Date(year, month, 1 + diff + (nth - 1) * 7);
        if (candidate.getMonth() === month && candidate >= today) return candidate;
        month++;
        if (month > 11) { month = 0; year++; }
      }
      return null;
    }
    return null;
  }

  // In edit mode use the frozen order; in create mode sort dynamically
  const orderedLocations = formMode === 'edit' && editModeLocationOrder.length > 0
    ? [
        ...editModeLocationOrder.map(id => locations.find(l => l.id === id)).filter(Boolean),
        ...locations.filter(l => !editModeLocationOrder.includes(l.id)),
      ]
    : [...locations].sort((a, b) => {
        const rank = (loc) => {
          const inc = formData.available_locations.includes(loc.id);
          if (!inc) return 2;
          const cfg = locationConfigs[loc.id] || {};
          const ok = !!cfg.venue_id && !!cfg.day_of_week && !!(cfg.week_of_month || cfg.weekly || cfg.fortnightly) && !!cfg.first_event_date;
          return ok ? 1 : 0;
        };
        return rank(a) - rank(b) || a.city.localeCompare(b.city);
      });

  return (
    <div className="admin-tab-container">
      <h3>{tabName}</h3>

      {error && <p className="error-message">{error}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}

      {/* Unsaved changes warning modal */}
      {showUnsavedWarning && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: '12px', padding: '28px 32px',
            maxWidth: '420px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ margin: '0 0 10px', fontSize: '1.15rem', fontWeight: '700' }}>
              Unsaved Changes
            </h3>
            <p style={{ margin: '0 0 20px', color: '#4b5563', fontSize: '0.92rem', lineHeight: 1.5 }}>
              You have unsaved changes to this theme. If you leave now your changes will be lost.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={cancelDiscard}
                style={{
                  padding: '8px 18px', borderRadius: '8px', border: '1.5px solid #d1d5db',
                  backgroundColor: '#fff', color: '#374151', fontWeight: '600',
                  fontSize: '0.9rem', cursor: 'pointer',
                }}
              >
                Keep Editing
              </button>
              <button
                onClick={confirmDiscard}
                style={{
                  padding: '8px 18px', borderRadius: '8px', border: 'none',
                  backgroundColor: '#dc2626', color: '#fff', fontWeight: '600',
                  fontSize: '0.9rem', cursor: 'pointer',
                }}
              >
                Discard Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '20px' }}>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          {showForm ? 'Hide Form' : 'Create Theme'}
        </button>
      </div>

      {/* Form */}
      {showForm && (
      <form
        onSubmit={handleSubmit}
        className="admin-form"
        style={{ paddingBottom: formMode === 'edit' ? '80px' : undefined }}
      >

        {/* Sticky "currently editing" banner — only in edit mode */}
        {formMode === "edit" && selectedTheme && (
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 100,
            background: 'linear-gradient(135deg, #5B21B6 0%, #8B5FBF 100%)',
            color: '#fff',
            padding: '14px 20px',
            borderRadius: '10px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 4px 16px rgba(91, 33, 182, 0.35)',
            gap: '12px',
            flexWrap: 'wrap',
          }}>
            <div>
              <div style={{ fontSize: '0.7rem', opacity: 0.75, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>
                ✏️ Currently editing
              </div>
              <div style={{ fontSize: '1.25rem', fontWeight: '800', letterSpacing: '-0.01em' }}>
                {formData.theme_name || selectedTheme.theme_name}
              </div>
              <div style={{ fontSize: '0.78rem', opacity: 0.75, marginTop: '2px' }}>
                ID #{selectedTheme.id} · {selectedTheme.theme_type}
              </div>
            </div>
            <button
              type="button"
              onClick={handleCancelClick}
              style={{
                padding: '8px 18px',
                borderRadius: '8px',
                border: '1.5px solid rgba(255,255,255,0.5)',
                backgroundColor: 'transparent',
                color: '#fff',
                fontWeight: '600',
                fontSize: '0.85rem',
                cursor: 'pointer',
                flexShrink: 0,
              }}
            >
              Cancel editing
            </button>
          </div>
        )}

        {formMode === "create" && (
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '20px' }}>Create Theme</h3>
        )}

        <div className="form-group">
          <label>Theme Name * (max 40 characters)</label>
          <input
            type="text"
            name="theme_name"
            value={formData.theme_name}
            onChange={handleChange}
            maxLength="40"
            required
          />
          <small style={{ display: 'block', marginTop: '4px', color: '#666' }}>
            {formData.theme_name.length}/40 characters
          </small>
        </div>

        <div className="form-group">
          <label>Theme Type *</label>
          <select
            name="theme_type"
            value={formData.theme_type}
            onChange={handleChange}
            required
          >
            <option value="">Select theme type...</option>
            <option value="casual">Casual</option>
            <option value="professional">Professional Networking</option>
            <option value="interest_based">Interest-Based</option>
            <option value="country">Countries</option>
          </select>
        </div>

        <div className="form-group">
          <label>
            <input
              type="checkbox"
              name="requires_subscription"
              checked={formData.requires_subscription}
              onChange={handleChange}
            />
            {" "}Requires Subscription
          </label>
        </div>

        {formMode === 'create' && (
          <div className="form-group">
            <label style={{ fontWeight: '700', fontSize: '1rem' }}>Locations</label>
            <div style={{
              backgroundColor: '#f3f4f6',
              border: '1px solid #9ca3af',
              borderRadius: '8px',
              padding: '0.75rem 1rem',
              margin: '4px 0 0',
              fontSize: '0.85rem',
              color: '#374151',
            }}>
              🚫 <strong>No longer in use - managed on Meetup.</strong> Venue and schedule data now comes from
              each landing page's linked Meetup group, not from OkGather. Kept here for reference only.
            </div>
          </div>
        )}

        {formMode === 'edit' && (
        <div className="form-group">
          <label style={{ fontWeight: '700', fontSize: '1rem' }}>Locations</label>
          <div style={{
            backgroundColor: '#f3f4f6',
            border: '1px solid #9ca3af',
            borderRadius: '8px',
            padding: '0.75rem 1rem',
            margin: '4px 0 12px',
            fontSize: '0.85rem',
            color: '#374151',
          }}>
            🚫 <strong>No longer in use - managed on Meetup.</strong> Venue and schedule data now comes from
            each landing page's linked Meetup group, not from OkGather. Left in place (not deleted) in case
            it's needed again - see git history to reinstate.
          </div>
          {orderedLocations.map((loc) => {
            const isIncluded = formData.available_locations.includes(loc.id);
            const cfg = locationConfigs[loc.id] || {};
            const locVenues = venues.filter(v => v.location_id === loc.id);
            const hasVenue = !!cfg.venue_id;
            const hasDay = !!cfg.day_of_week;
            const hasSchedule = !!(cfg.week_of_month || cfg.weekly || cfg.fortnightly);
            const hasFirstDate = !!cfg.first_event_date;
            const isActive = isIncluded && hasVenue && hasDay && hasSchedule && hasFirstDate;
            const isIncomplete = isIncluded && !isActive;
            // Show "it's ready" nudge only for locations that just became active during this edit session
            const justBecameActive = formMode === 'edit' && isActive && !originalActiveLocIds.has(loc.id);

            return (
              <div key={loc.id} style={{
                border: `2px solid ${isActive ? '#16a34a' : isIncomplete ? '#f59e0b' : '#e5e7eb'}`,
                borderRadius: '10px',
                marginBottom: '10px',
                overflow: 'hidden',
              }}>
                {/* ── Card header ── */}
                {!isIncluded ? (
                  /* Non-included: entire header is a click target to enable */
                  <button
                    type="button"
                    onClick={() => handleLocationToggle(loc.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '8px',
                      padding: '10px 14px',
                      backgroundColor: '#f9fafb',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0f0ff'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = '#f9fafb'}
                  >
                    <span style={{ fontWeight: '700', color: '#374151', fontSize: '0.95rem' }}>
                      📍 {loc.city}{loc.area ? ` — ${loc.area}` : ''}
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '6px 16px',
                      borderRadius: '20px',
                      backgroundColor: '#5B21B6',
                      color: '#fff',
                      fontWeight: '700',
                      fontSize: '0.82rem',
                    }}>
                      ＋ Enable for this theme
                    </span>
                  </button>
                ) : (
                  /* Included: static header with an "× Remove" control */
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '8px',
                    padding: '10px 14px',
                    backgroundColor: isActive ? '#f0fdf4' : '#fffbeb',
                  }}>
                    <span style={{ fontWeight: '700', color: '#374151', fontSize: '0.95rem' }}>
                      📍 {loc.city}{loc.area ? ` — ${loc.area}` : ''}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      {isActive && (
                        <span style={{ color: '#16a34a', fontWeight: '700', fontSize: '0.8rem' }}>✅ Active</span>
                      )}
                      {isIncomplete && (
                        <span style={{ color: '#b45309', fontWeight: '700', fontSize: '0.8rem' }}>⚠️ Incomplete</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleLocationToggle(loc.id)}
                        style={{
                          padding: '5px 14px',
                          borderRadius: '20px',
                          border: '1.5px solid #d1d5db',
                          backgroundColor: '#fff',
                          color: '#6b7280',
                          fontWeight: '600',
                          fontSize: '0.8rem',
                          cursor: 'pointer',
                        }}
                      >
                        × Remove
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Fields (only when included) ── */}
                {isIncluded && (
                  <div style={{ padding: '12px 14px', borderTop: '1px solid #e5e7eb', backgroundColor: '#fff' }}>
                    {isIncomplete && (
                      <div style={{
                        backgroundColor: '#fef3c7',
                        border: '1px solid #f59e0b',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        marginBottom: '12px',
                        fontSize: '0.82rem',
                        color: '#92400e',
                        fontWeight: '600',
                      }}>
                        ⚠️ Set the venue, day, schedule, and first event date below to make this location active.
                      </div>
                    )}
                    {justBecameActive && (
                      <div style={{
                        backgroundColor: '#dcfce7',
                        border: '1px solid #4ade80',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        marginBottom: '12px',
                        fontSize: '0.82rem',
                        color: '#166534',
                        fontWeight: '700',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}>
                        ✅ This location is ready — now click <span style={{ textDecoration: 'underline' }}>Update Theme</span> to save.
                      </div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>

                      {/* Venue */}
                      <div style={{ flex: '1 1 200px' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          Venue <span style={{ color: '#ef4444' }}>*</span>
                          {!hasVenue && <span style={{ color: '#ef4444', marginLeft: '6px', fontWeight: '400' }}>required</span>}
                        </label>
                        <select
                          value={cfg.venue_id || ''}
                          onChange={e => updateLocationConfig(loc.id, 'venue_id', e.target.value)}
                          style={{
                            width: '100%', padding: '6px', borderRadius: '4px', fontSize: '0.85rem',
                            border: `1.5px solid ${!hasVenue ? '#ef4444' : '#ccc'}`,
                          }}
                        >
                          <option value="">— Select venue —</option>
                          {locVenues.length === 0 && (
                            <option disabled>No venues in this city yet</option>
                          )}
                          {locVenues.map(v => (
                            <option key={v.id} value={v.id}>
                              {v.name}{v.address ? ` | ${v.address}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Day of Week */}
                      <div style={{ flex: '1 1 150px' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>
                          Day of Week <span style={{ color: '#ef4444' }}>*</span>
                          {!hasDay && <span style={{ color: '#ef4444', marginLeft: '6px', fontWeight: '400' }}>required</span>}
                        </label>
                        <select
                          value={cfg.day_of_week || ''}
                          onChange={e => updateLocationConfig(loc.id, 'day_of_week', e.target.value)}
                          style={{
                            width: '100%', padding: '6px', borderRadius: '4px', fontSize: '0.85rem',
                            border: `1.5px solid ${!hasDay ? '#ef4444' : '#ccc'}`,
                          }}
                        >
                          <option value="">— Select day —</option>
                          {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => (
                            <option key={d} value={d}>{d}</option>
                          ))}
                        </select>
                      </div>

                      {/* Frequency picker */}
                      <div style={{ flex: '1 1 280px' }}>
                        <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '6px' }}>
                          Frequency <span style={{ color: '#ef4444' }}>*</span>
                          {!hasSchedule && <span style={{ color: '#ef4444', marginLeft: '6px', fontWeight: '400' }}>required</span>}
                        </label>
                        {/* Segmented frequency buttons */}
                        <div style={{ display: 'flex', borderRadius: '8px', border: `1.5px solid ${!hasSchedule ? '#ef4444' : '#ccc'}`, overflow: 'hidden', marginBottom: '8px' }}>
                          {[
                            { key: 'monthly',     label: 'Monthly' },
                            { key: 'fortnightly', label: 'Fortnightly' },
                            { key: 'weekly',      label: 'Weekly' },
                          ].map(opt => {
                            const active =
                              opt.key === 'weekly'      ? !!cfg.weekly :
                              opt.key === 'fortnightly' ? !!cfg.fortnightly :
                              !cfg.weekly && !cfg.fortnightly;
                            return (
                              <button
                                key={opt.key}
                                type="button"
                                onClick={() => {
                                  updateLocationConfig(loc.id, 'weekly',      opt.key === 'weekly');
                                  updateLocationConfig(loc.id, 'fortnightly', opt.key === 'fortnightly');
                                  if (opt.key !== 'monthly') updateLocationConfig(loc.id, 'week_of_month', '');
                                }}
                                style={{
                                  flex: 1,
                                  padding: '7px 4px',
                                  border: 'none',
                                  borderRight: opt.key !== 'weekly' ? '1px solid #ccc' : 'none',
                                  backgroundColor: active ? '#8B5FBF' : '#fff',
                                  color: active ? '#fff' : '#374151',
                                  fontWeight: active ? '700' : '500',
                                  fontSize: '0.82rem',
                                  cursor: 'pointer',
                                  transition: 'background 0.15s',
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                        {/* Week of month — only shown for Monthly */}
                        {!cfg.weekly && !cfg.fortnightly && (
                          <div>
                            <input
                              type="number"
                              min="1" max="5"
                              value={cfg.week_of_month || ''}
                              onWheel={e => e.target.blur()}
                              onChange={e => updateLocationConfig(loc.id, 'week_of_month', e.target.value)}
                              placeholder="Which week? (1 = first, 2 = second…)"
                              style={{
                                width: '100%', padding: '6px', borderRadius: '4px', fontSize: '0.85rem',
                                border: `1.5px solid ${!cfg.week_of_month ? '#ef4444' : '#ccc'}`,
                                boxSizing: 'border-box',
                              }}
                            />
                          </div>
                        )}
                      </div>

                    </div>

                    {/* First event date + next event */}
                    {(() => {
                      const dateError = (() => {
                        if (!cfg.first_event_date || !cfg.day_of_week) return null;
                        const d = new Date(cfg.first_event_date + 'T00:00:00');
                        if (isNaN(d.getTime())) return null;
                        const actualDay = DAYS[d.getDay()];
                        if (actualDay !== cfg.day_of_week) {
                          return `This date is a ${actualDay}, but you've selected ${cfg.day_of_week}. Please choose a ${cfg.day_of_week}.`;
                        }
                        return null;
                      })();

                      const nextEvent = !dateError
                        ? computeNextEvent(cfg.first_event_date, cfg.day_of_week, cfg.weekly, cfg.fortnightly, cfg.week_of_month)
                        : null;

                      return (
                        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-start' }}>
                          <div style={{ flex: '1 1 200px' }}>
                            <label style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', display: 'block', marginBottom: '4px' }}>
                              First Event Date <span style={{ color: '#ef4444' }}>*</span>
                              {!hasFirstDate && <span style={{ color: '#ef4444', marginLeft: '6px', fontWeight: '400' }}>required</span>}
                            </label>
                            <input
                              type="date"
                              value={cfg.first_event_date || ''}
                              onChange={e => updateLocationConfig(loc.id, 'first_event_date', e.target.value)}
                              style={{
                                width: '100%', padding: '6px', borderRadius: '4px', fontSize: '0.85rem',
                                border: `1.5px solid ${dateError || !hasFirstDate ? '#ef4444' : '#ccc'}`,
                                boxSizing: 'border-box',
                              }}
                            />
                            {dateError && (
                              <div style={{ marginTop: '4px', fontSize: '0.78rem', color: '#dc2626', fontWeight: '600' }}>
                                ⚠️ {dateError}
                              </div>
                            )}
                          </div>
                          {nextEvent && (
                            <div style={{ flex: '1 1 200px', padding: '8px 12px', backgroundColor: '#f0fdf4', border: '1px solid #86efac', borderRadius: '6px', fontSize: '0.82rem', color: '#15803d' }}>
                              <div style={{ fontWeight: '700', marginBottom: '2px' }}>📅 Next scheduled event</div>
                              <div>{nextEvent.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            );
          })}
          {locations.length === 0 && (
            <p style={{ fontSize: '0.85rem', color: '#9ca3af' }}>No active locations found. Add locations in the Locations tab first.</p>
          )}
        </div>
        )}

        <div className="form-group">
          <label htmlFor="image_url">Theme Image URL</label>
          <input
            type="text"
            id="image_url"
            name="image_url"
            value={formData.image_url}
            onChange={handleChange}
            placeholder="https://example.com/image.jpg"
          />
          <p style={{ fontSize: "12px", color: "#666", marginTop: "5px" }}>
            Paste an external image URL (e.g., from Imgur, Google Photos, etc.)
          </p>
          {formData.image_url && (
            <div style={{ marginTop: "10px" }}>
              <p style={{ fontSize: "12px", color: "#666", marginBottom: "5px" }}>Preview:</p>
              <img
                src={formData.image_url}
                alt="Theme preview"
                onError={(e) => { e.target.style.display = 'none'; }}
                style={{ maxWidth: "200px", maxHeight: "200px", border: "1px solid #ccc", borderRadius: "4px" }}
              />
            </div>
          )}
        </div>

        <div className="form-group">
          <label>Default Landing Page Description</label>
          <p style={{ fontSize: "12px", color: "#666", marginTop: "5px", marginBottom: "10px" }}>
            Starting-point rich text for the Description field on a brand-new landing page for
            this theme (e.g. shared boilerplate for A/B test variants). Only applies when creating
            a fresh landing page - adding a second A/B variant to an existing page always copies
            that page's own Description instead, and this can still be edited per landing page.
          </p>
          <Suspense fallback={
            <div style={{
              padding: '20px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              textAlign: 'center',
              color: '#666'
            }}>
              Loading editor...
            </div>
          }>
            <RichTextEditor
              value={formData.default_description}
              onChange={(value) => setFormData(prev => ({ ...prev, default_description: value }))}
            />
          </Suspense>
        </div>

        {/* Inline button-group (shown in create mode; hidden in edit mode where the sticky footer takes over) */}
        {formMode === "create" && (
          <div className="button-group">
            <button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Create Theme"}
            </button>
          </div>
        )}

        {/* Sticky footer for edit mode */}
        {formMode === "edit" && (
          <div style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            backgroundColor: '#fff',
            borderTop: '2px solid #e5e7eb',
            padding: '12px 24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            alignItems: 'center',
            boxShadow: '0 -4px 16px rgba(0,0,0,0.08)',
          }}>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 28px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: isDirty ? '#5B21B6' : '#9ca3af',
                color: '#fff',
                fontWeight: '700',
                fontSize: '0.95rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'background 0.2s',
                minWidth: '140px',
              }}
            >
              {loading ? 'Saving...' : 'Update Theme'}
            </button>
            <button
              type="button"
              onClick={handleCancelClick}
              style={{
                padding: '10px 24px',
                borderRadius: '8px',
                border: '1.5px solid #d1d5db',
                backgroundColor: '#fff',
                color: '#374151',
                fontWeight: '600',
                fontSize: '0.95rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        )}
      </form>
      )}

      {/* Themes List */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '16px', flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
            Existing Themes
          </h3>
          {themes.filter(t => !t.has_landing_page).length > 0 && (
            <button
              onClick={() => setShowInactive(prev => !prev)}
              style={{
                padding: '6px 14px',
                backgroundColor: showInactive ? '#6b7280' : '#f3f4f6',
                color: showInactive ? '#fff' : '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: '600',
              }}
            >
              {showInactive
                ? 'Hide Inactive Themes'
                : `Show Inactive Themes (${themes.filter(t => !t.has_landing_page).length})`}
            </button>
          )}
        </div>
        {loading && <p>Loading...</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {themes
            .filter(t => showInactive || t.has_landing_page)
            .sort((a, b) => a.theme_name.localeCompare(b.theme_name))
            .map((theme) => (
              <div key={theme.id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '10px',
                padding: '14px 16px',
                backgroundColor: '#fff',
              }}>
                {/* Top row: name + status badge */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <div>
                    <span style={{ fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>
                      {theme.theme_name}
                    </span>
                    <span style={{ marginLeft: '8px', fontSize: '0.75rem', color: '#6b7280' }}>
                      #{theme.id}
                    </span>
                  </div>
                  {theme.has_landing_page ? (
                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#16A34A', whiteSpace: 'nowrap' }}>✓ Live</span>
                  ) : (
                    <span style={{ fontSize: '0.78rem', fontWeight: '700', color: '#DC2626', whiteSpace: 'nowrap' }}>⚠ No Landing Page</span>
                  )}
                </div>

                {/* Metadata pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  {[
                    { label: theme.theme_type || 'no type' },
                    { label: theme.requires_subscription ? 'Sub required' : 'No sub required' },
                    {
                      label: theme.active_location_count > 0
                        ? `${theme.active_location_count} active location${theme.active_location_count === 1 ? '' : 's'}`
                        : 'No active locations',
                      green: theme.active_location_count > 0,
                    },
                  ].map((pill, i) => (
                    <span key={i} style={{
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      backgroundColor: pill.green ? '#dcfce7' : '#f3f4f6',
                      color: pill.green ? '#15803d' : '#374151',
                    }}>
                      {pill.label}
                    </span>
                  ))}
                </div>

                {/* Action buttons — wrap naturally on narrow screens */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  <button
                    onClick={() => startEdit(theme)}
                    style={{
                      padding: '6px 14px', borderRadius: '6px', border: '1.5px solid #5B21B6',
                      backgroundColor: '#fff', color: '#5B21B6', fontWeight: '600',
                      fontSize: '0.82rem', cursor: 'pointer',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleClone(theme)}
                    style={{
                      padding: '6px 14px', borderRadius: '6px', border: 'none',
                      backgroundColor: '#10B981', color: '#fff', fontWeight: '600',
                      fontSize: '0.82rem', cursor: 'pointer',
                    }}
                  >
                    Clone
                  </button>
                  {!theme.has_landing_page && (
                    <button
                      onClick={() => handleCreateLandingPage(theme)}
                      style={{
                        padding: '6px 14px', borderRadius: '6px', border: 'none',
                        backgroundColor: '#F59E0B', color: '#fff', fontWeight: '600',
                        fontSize: '0.82rem', cursor: 'pointer',
                      }}
                    >
                      Create LP
                    </button>
                  )}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export default AdminThemes;
